import React, { useEffect, useRef } from 'react';
import styled from '@emotion/styled';
import { BodyText } from '@/shared/components/StyledText';
import { darkGreen, mint } from '@/shared/styles/colors';
import {
  borderRadius,
  FONT_STYLES,
  WELCOME_PROMO_VIDEO_MAX_WIDTH,
  WELCOME_PROMO_VIDEO_URL,
  WELCOME_SECTION_BODY_COPY,
  WELCOME_SECTION_GAP_NARROW,
  WELCOME_SECTION_GAP_WIDE,
  WELCOME_SECTION_HORIZONTAL_BASE,
  WELCOME_SECTION_HORIZONTAL_RHYTHM_EXTRA,
  WELCOME_SECTION_MAX_WIDTH,
  WELCOME_SECTION_VERTICAL_BASE,
  spacing,
} from '@/shared/styles/constants';
import { usePrefersReducedMotion } from '@/shared/hooks/usePrefersReducedMotion';
import { useWelcomeSectionLayout } from '@/shared/hooks/useWelcomeSectionLayout';
import { ABOUT_COPY } from '@/shared/constants/aboutCopy';

/** Title → body in the About column; wide matches other welcome sections. */
const ABOUT_TITLE_TO_BODY_GAP_WIDE = 20;
const ABOUT_TITLE_TO_BODY_GAP_NARROW = 20;

interface LayoutProps {
  isWideLayout: boolean;
}

interface AboutBodyParagraphProps {
  copyAlignLeft: boolean;
}

const WelcomeVideoSection = () => {
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const preferReducedMotion = usePrefersReducedMotion();
  const { isWide: isWideLayout } = useWelcomeSectionLayout();
  const shouldLeftAlignAboutHeadline = isWideLayout;

  useEffect(() => {
    if (preferReducedMotion) {
      return;
    }
    const video = videoRef.current;
    if (!video) {
      return;
    }
    const play = () => {
      video.muted = true;
      void video.play().catch(() => {});
    };
    play();
    video.addEventListener('canplay', play, { once: true });
    return () => video.removeEventListener('canplay', play);
  }, [preferReducedMotion]);

  return (
    <Section isWideLayout={isWideLayout}>
      <SectionInner isWideLayout={isWideLayout}>
        <VideoWrapper isWideLayout={isWideLayout}>
          <StyledVideo
            ref={videoRef}
            src={WELCOME_PROMO_VIDEO_URL}
            autoPlay={!preferReducedMotion}
            muted
            loop
            playsInline
            preload="auto"
            controls
            aria-label="Welcome video: short intro to virtual styling with EMME"
          />
        </VideoWrapper>
        <CopyColumn
          isWideLayout={isWideLayout}
          copyAlignLeft={shouldLeftAlignAboutHeadline}
        >
          <AboutTitleRow>
            <AboutTitleText copyAlignLeft={shouldLeftAlignAboutHeadline}>
              {ABOUT_COPY.title}
            </AboutTitleText>
          </AboutTitleRow>
          <AboutBodyStack copyAlignLeft={shouldLeftAlignAboutHeadline}>
            {ABOUT_COPY.paragraphs.map((text) => (
              <AboutBodyParagraph
                key={text}
                copyAlignLeft={shouldLeftAlignAboutHeadline}
              >
                {text}
              </AboutBodyParagraph>
            ))}
          </AboutBodyStack>
        </CopyColumn>
      </SectionInner>
    </Section>
  );
};

const Section = styled.div<LayoutProps>(({ isWideLayout }) => ({
  width: '100%',
  backgroundColor: mint,
  paddingTop: isWideLayout ? 100 : WELCOME_SECTION_VERTICAL_BASE,
  paddingBottom: isWideLayout ? 100 : WELCOME_SECTION_VERTICAL_BASE,
  paddingLeft: isWideLayout
    ? WELCOME_SECTION_HORIZONTAL_BASE
    : WELCOME_SECTION_HORIZONTAL_RHYTHM_EXTRA,
  paddingRight: isWideLayout
    ? WELCOME_SECTION_HORIZONTAL_BASE
    : WELCOME_SECTION_HORIZONTAL_RHYTHM_EXTRA,
  boxSizing: 'border-box',
}));

const SectionInner = styled.div<LayoutProps>(({ isWideLayout }) => ({
  width: '100%',
  maxWidth: isWideLayout ? WELCOME_SECTION_MAX_WIDTH : undefined,
  marginLeft: isWideLayout ? 'auto' : undefined,
  marginRight: isWideLayout ? 'auto' : undefined,
  display: 'flex',
  flexDirection: isWideLayout ? 'row' : 'column',
  alignItems: 'center',
  justifyContent: isWideLayout ? 'center' : 'flex-start',
  gap: isWideLayout ? WELCOME_SECTION_GAP_WIDE : spacing.md,
}));

interface CopyColumnProps extends LayoutProps {
  copyAlignLeft: boolean;
}

const CopyColumn = styled.div<CopyColumnProps>(
  ({ isWideLayout, copyAlignLeft }) => ({
    flexShrink: isWideLayout ? 1 : undefined,
    minWidth: 0,
    maxWidth: isWideLayout ? 520 : undefined,
    width: '100%',
    alignItems: copyAlignLeft ? 'flex-start' : 'center',
    display: 'flex',
    flexDirection: 'column',
    gap: isWideLayout
      ? ABOUT_TITLE_TO_BODY_GAP_WIDE
      : ABOUT_TITLE_TO_BODY_GAP_NARROW,
    paddingTop: isWideLayout ? undefined : spacing.md,
  }),
);

const AboutTitleRow = styled.div({
  width: '100%',
  alignSelf: 'stretch',
  boxSizing: 'border-box',
});

const AboutTitleText = styled('h2')<{ copyAlignLeft: boolean }>(
  ({ copyAlignLeft }) => ({
    margin: 0,
    ...FONT_STYLES.lora,
    color: darkGreen,
    fontSize: 40,
    lineHeight: '40.6px',
    fontWeight: 400,
    textAlign: copyAlignLeft ? 'left' : 'center',
    textTransform: 'uppercase',
    letterSpacing: 1,
    width: '100%',
  }),
);

/** Vertical rhythm between paragraphs (section row-gap scale). */
const AboutBodyStack = styled.div<{ copyAlignLeft: boolean }>(
  ({ copyAlignLeft }) => ({
    display: 'flex',
    flexDirection: 'column',
    alignItems: copyAlignLeft ? 'flex-start' : 'center',
    gap: WELCOME_SECTION_GAP_NARROW,
    width: '100%',
  }),
);

const AboutBodyParagraph = styled(BodyText)<AboutBodyParagraphProps>(
  ({ copyAlignLeft }) => ({
    color: darkGreen,
    ...FONT_STYLES.welcomeHelveticaNeue,
    fontSize: WELCOME_SECTION_BODY_COPY.fontSize,
    fontWeight: '300',
    letterSpacing: WELCOME_SECTION_BODY_COPY.letterSpacing,
    lineHeight: 24,
    textAlign: copyAlignLeft ? 'left' : 'center',
    marginBottom: 0,
    width: '100%',
  }),
);

const VideoWrapper = styled.div<LayoutProps>(({ isWideLayout }) => ({
  width: '100%',
  maxWidth: WELCOME_PROMO_VIDEO_MAX_WIDTH,
  flexShrink: isWideLayout ? 0 : undefined,
  alignSelf: isWideLayout ? undefined : 'center',
}));

const StyledVideo = styled.video({
  width: '100%',
  height: 'auto',
  display: 'block',
  borderRadius: borderRadius.lg,
});

export default WelcomeVideoSection;
